import { useState } from "react";
import {
  PLANS,
  MASTERY_AXES,
  BASE_MIN,
  BASE_MAX,
  PROFESSIONS_NOTEES,
  computeExposure,
} from "@riseafrica/scoring";
import type { TaskLevel } from "@riseafrica/scoring";
import { SUGGESTS_INDEPENDANTS, SUGGESTS_VISIBLES } from "../content/copy";
import { fmt } from "../lib/format";
import { track } from "../lib/track";
import { useReveal } from "../lib/useReveal";
import { messageSecondDiagnostic } from "../content/secondDiagnostic";
import { useApp } from "../state/AppState";
import { ApercuAction } from "../components/ApercuAction";
import { RappelAchat } from "../components/RappelAchat";
import { RecupererRapport } from "../components/RecupererRapport";
import { ApresPaiement } from "../components/ApresPaiement";
import { Offers } from "../components/Offers";
import { Temoignages } from "../components/Temoignages";
import { Ticker } from "../components/Ticker";
import { Usage } from "../components/Usage";

/**
 * La question qui ne se pose que si le mode CV est ouvert.
 *
 * Laissée en place alors que le mode est fermé, elle promet un écran qui
 * n'existe pas — et rassurer sur le sort d'un document qu'on ne demande jamais
 * est exactement le genre de texte qui fait douter de tout le reste.
 */
const FAQ_CV = "Que devient mon CV ?";

/** Retirée quand l'axe dirigeant est fermé : elle renvoie vers une porte absente. */
const FAQ_DIRIGEANT = "Je dirige une entreprise. Que mesure le diagnostic pour moi ?";

/*
  Une réponse peut porter un lien — 26/08/2026.

  Deux des questions renvoient à une page du site : la garantie mène à « un
  problème, une demande », d'où la demande de remboursement part réellement, et
  la question sur l'IA mène au barème publié. Les nommer sans y mener laisserait
  le lecteur les chercher dans le pied de page, sur la liste où il pèse
  justement s'il achète.

  Le lien vit à côté de la réponse plutôt que dedans : une réponse est une
  chaîne, et y coudre du JSX obligerait à écrire chaque texte en morceaux.
*/
interface QuestionFrequente {
  readonly question: string;
  readonly reponse: string;
  /** La page où agir, quand il y en a une. */
  readonly lien?: { readonly href: string; readonly texte: string };
}

/**
 * Le prix dirigeant ne s'écrit pas en dur dans une réponse — 09/09/2026.
 *
 * Ce prix est **posable depuis `/pilotage`** : `reglages.prixEntreprise` borne
 * entre 5 000 et 60 000 F, et le commentaire qui pose ces bornes annonce déjà
 * l'intention de « le porter à 25 000 F ». La fenêtre de paiement, l'appel à
 * l'action dirigeant et l'encaissement lisent tous la valeur posée ; deux
 * réponses de cette liste, elles, affirmaient « 15 000 F » en toutes lettres.
 *
 * Le jour où le prix change, la personne lit 15 000 F dans les questions
 * fréquentes — c'est-à-dire à l'endroit exact où l'on va vérifier un prix avant
 * de décider — puis se voit demander autre chose. Une divergence de prix sur
 * une page commerciale n'est pas une coquille : c'est ce qui fait renoncer, et
 * ce qui fait écrire au service client.
 *
 * Le jeton est remplacé au rendu par le prix réellement en vigueur.
 */
const JETON_PRIX_ENTREPRISE = "{prixEntreprise}";

/** La réponse, avec le prix dirigeant du moment à la place du jeton. */
export function avecPrixEntreprise(reponse: string, prix: number): string {
  return reponse.split(JETON_PRIX_ENTREPRISE).join(fmt(prix));
}

const FAQ: ReadonlyArray<QuestionFrequente> = [
  {
    question: "Mon métier n'est dans aucune liste. Ça marche quand même ?",
    reponse: "Oui. Il n'y a pas de liste fermée : vous tapez votre métier, quel qu'il soit, et les tâches types sont établies pour lui. Vétérinaire, sage-femme, agent immobilier, chef de chantier — tous passent.",
  },
  {
    question: "Mon employeur peut-il voir mon résultat ?",
    reponse: "Non. Nous ne contactons aucune entreprise, nous ne demandons pas où vous travaillez, et rien n'est publié. Votre résultat s'affiche sur votre écran ; c'est vous qui décidez de l'envoyer à quelqu'un. Si vous ne voulez laisser aucune trace, faites le diagnostic sans donner de numéro ni de nom : tout fonctionne quand même.",
  },
  {
    question: "Qui est derrière ce diagnostic ?",
    reponse: "STRATERA est édité par RISEAFRICA, qui conçoit des outils numériques pour les actifs d'Afrique francophone — dont Propulse, un accompagnement de carrière. Nous ne sommes ni un cabinet de recrutement ni un organisme de formation, et nous ne revendons aucune donnée : notre modèle est la vente de rapports — 3 000 F pour un actif, {prixEntreprise} F pour une entreprise — rien d'autre.",
  },
  {
    question: "Que devient mon CV ?",
    reponse: "Il est analysé au moment où vous cliquez, puis abandonné. Nous ne le stockons pas et ne le transmettons à personne. Vous pouvez d'ailleurs obtenir votre indice sans fournir aucun document.",
  },
  {
    question: "Est-ce que vous vendez de la peur ?",
    reponse: "Nous publions notre méthode et nos limites, ce qui est le contraire du procédé. Un indice élevé ne veut pas dire perdre son emploi : il veut dire que le contenu de votre travail va changer. Toute la différence tient à ce que vous ferez dans les six prochains mois.",
  },
  {
    /*
      L'objection la plus prévisible d'un produit qui vend un diagnostic d'IA,
      et qui n'était posée nulle part — 26/08/2026.

      La réponse n'a pas à être défensive : c'est le seul endroit du marché où
      elle est vérifiable. Le score ne vient jamais du modèle, la méthode est
      publiée, et ce que le modèle écrit vraiment est dit sans détour plutôt que
      dissimulé. Une réponse qui prétendrait « aucune IA n'intervient » serait
      fausse — et fausse sur le point exact où l'on demande d'être cru.
    */
    question: "Est-ce qu'une IA écrit mon rapport ?",
    reponse: "En partie, et voici exactement laquelle. Votre indice n'est jamais calculé par un modèle : il est calculé par du code, à partir d'un barème publié et versionné que vous pouvez lire, refaire à la main et contester. Le modèle sert à deux choses — reconnaître les tâches de votre métier, et rédiger les dix actions et les cinq automatisations sur ces tâches-là. Ce qu'il rend passe ensuite par des contrôles automatiques : chaque automatisation doit tenir en trois étapes et dire quand elle se déclenche, sinon elle est refusée, et aucun gain annoncé ne peut dépasser une journée de travail par semaine. Et si le modèle échoue, vous obtenez quand même un résultat, signalé comme générique plutôt que présenté comme le vôtre.",
    lien: { href: "/methode", texte: "Lire la méthode et le barème" },
  },
  {
    /*
      Le vrai concurrent n'était pas dans le comparatif — 28/09/2026.

      Le tableau oppose le diagnostic aux vidéos et aux formations. La
      comparaison que le visiteur fait réellement est ailleurs : « je peux
      poser la question moi-même à un assistant, gratuitement ». Ne pas y
      répondre, c'est la laisser se conclure sans nous.

      La réponse ne dénigre pas l'outil — le volet 3 mesure justement si la
      personne sait s'en servir — et ne nomme aucune marque. Elle ne dit que
      ce qui est vérifiable : l'indice est un calcul, donc il se répète ; le
      coût vient des heures déclarées ; l'échelle est la même pour tous.
    */
    question: "Pourquoi ne pas simplement demander à un assistant IA ?",
    reponse: "Vous pouvez, et c'est même une bonne habitude. Mais posez-lui deux fois la même question : vous aurez deux réponses. Ici, l'indice est un calcul, pas une réponse — à tâches et temps égaux, il est le même, pour vous comme pour votre collègue, ce qui permet de le comparer et de le refaire dans trois mois pour voir s'il a bougé. Le coût est chiffré en francs à partir des heures que vous déclarez, pas estimé au hasard. Et si vous savez déjà bien vous servir d'un assistant, le troisième volet vous le dira : c'est exactement ce qu'il mesure.",
    lien: { href: "/methode", texte: "Voir comment l'indice est calculé" },
  },
  {
    question: "Pourquoi 3 000 F et pas gratuit ?",
    reponse: "Parce qu'un plan d'action gratuit n'est jamais appliqué, et parce que chaque analyse a un coût réel. Le diagnostic reste gratuit : vous ne payez que si vous voulez la suite, et après avoir vu la valeur du gratuit.",
  },
  {
    /*
      La garantie existait partout sauf ici — 26/08/2026.

      Elle était écrite dans la fenêtre de paiement, sur l'écran d'après-achat,
      sous le plan d'actions, dans les mentions légales et une fois à l'accueil.
      C'est-à-dire nulle part dans la liste où l'on va chercher ses objections
      **avant** de décider. Une garantie qu'on ne rencontre qu'après avoir sorti
      son téléphone ne lève rien : elle rassure quelqu'un qui a déjà tranché.
    */
    question: "Et si le rapport ne m'apprend rien ?",
    reponse: "Il est remboursé, intégralement, sans justification à fournir, dans les quatorze jours. Vous n'avez pas à expliquer pourquoi, et personne ne vous le demandera : la demande se fait depuis la page « un problème, une demande », elle est enregistrée et datée, et le remboursement repart par le moyen de paiement utilisé. C'est aussi la raison pour laquelle le diagnostic est gratuit et le début de votre première action offert, avec une action entière montrée en exemple sur cette page : vous voyez ce que vaut le travail avant de payer, et vous pouvez revenir dessus après.",
    lien: { href: "/aide", texte: "Demander le remboursement" },
  },
  {
    question: "Je suis à mon compte. Est-ce que ça me concerne ?",
    reponse: "Plus que les salariés, souvent — et pas seulement comme une menace. Choisissez « à mon compte » au-dessus du champ, et le diagnostic se lit deux fois : ce que vos clients peuvent désormais faire eux-mêmes avec une IA, et ce que vous pouvez produire en une heure au lieu de quatre. Ce temps-là ne revient à aucun employeur. Le rapport porte alors sur ce que vous pouvez systématiser pour que votre activité tienne sans y passer vos soirées, et sur le repositionnement de votre offre — ce que vous vendez, à qui, à quel prix — au lieu d'un poste à viser.",
  },
  {
    question: FAQ_DIRIGEANT,
    reponse: "Autre chose, et par une autre porte : choisissez « je dirige » en haut de la page. Vous ne mesurez pas votre poste mais le temps que vos équipes passent sur des postes répétitifs — en heures, en personnes à temps plein, et en francs par mois. Le diagnostic est gratuit ; le rapport de votre entreprise, qui contient dix actions à confier et le plan des trente premiers jours, est à {prixEntreprise} F. Aucun nom d'employé n'est demandé, et le salaire moyen que vous saisissez ne quitte pas votre appareil — sauf si vous cochez vous-même la case qui en transmet le coût horaire pour le rapport.",
  },
  {
    question: "Combien de temps mon résultat reste-t-il valable ?",
    reponse: "Environ trois mois. Deux choses le déplacent : les outils, qui avancent, et surtout vous — dès que vous confiez une tâche à une IA, votre répartition du temps change, donc votre indice aussi. C'est pour cela que le rapport fixe un point à quatre-vingt-dix jours et pose le rappel dans votre agenda. Refaire le diagnostic est gratuit : il se rouvre de lui-même à quatre-vingt-dix jours, ou tout de suite si deux personnes passent le test depuis votre lien. Le refaire avant d'avoir changé quelque chose n'apprendrait rien.",
  },
];

/** Colonnes du comparatif. La dernière est la nôtre. */
const COLONNES = ["Vidéos gratuites", "Formation IA générique", "Ce diagnostic"] as const;

type Ton = "" | "no" | "yes";

const COMPARATIF: ReadonlyArray<{
  critere: string;
  cellules: readonly [readonly [string, Ton], readonly [string, Ton], readonly [string, Ton]];
}> = [
  {
    critere: "Ce que vous obtenez",
    cellules: [
      ["Des astuces d'outils", ""],
      ["Des bases théoriques", ""],
      ["L'état réel de votre métier", ""],
    ],
  },
  {
    critere: "Adapté à votre métier",
    cellules: [
      ["Non", "no"],
      ["Rarement", "no"],
      ["Oui, tâche par tâche", "yes"],
    ],
  },
  {
    critere: "Temps à y consacrer",
    cellules: [
      ["Des dizaines d'heures", ""],
      ["2 à 5 jours", ""],
      ["Moins de 2 min le premier volet, moins de 5 min les trois", ""],
    ],
  },
  {
    critere: "Prix",
    cellules: [
      ["Gratuit", ""],
      ["80 000 à 250 000 F", ""],
      ["Gratuit, puis 3 000 F", ""],
    ],
  },
  {
    critere: "Vous dit quoi arrêter de faire",
    cellules: [
      ["Non", "no"],
      ["Non", "no"],
      ["Oui", "yes"],
    ],
  },
  {
    /*
      « Second diagnostic à 90 jours » présentait comme un avantage du produit
      quelque chose qui est gratuit et sans condition pour tout le monde : le
      diagnostic est librement répétable, aujourd'hui, demain et dans trois
      mois. La cellule promettait donc un déverrouillage qui n'existe nulle
      part — la règle n° 6 à l'endroit le plus visible du site, la colonne qui
      justifie le prix.

      Ce qui est vrai, et vérifiable dans le code : l'attestation de niveau
      porte un numéro de série, et le volet 3 compare deux passages — c'est
      `t3.progression`, « vous passez de X à Y ». C'est le seul endroit du
      produit où « ce qui a bougé » veut dire quelque chose, et il ne coûte
      rien. L'indice, lui, n'est pas une mesure de progrès : il dit ce qu'une
      IA sait faire de votre métier, et automatiser une tâche ne le fait pas
      baisser.
    */
    critere: "Mesure vos progrès",
    cellules: [
      ["Non", "no"],
      ["Non", "no"],
      ["Oui, d'une attestation à la suivante", "yes"],
    ],
  },
];

const LIMITES = [
  "Il ne prédit pas de licenciement. Il mesure l'exposition de vos tâches, pas la décision de votre employeur ni celle de vos clients.",
  "Il ne remplace pas un bilan de compétences. Il est plus rapide, plus étroit, centré sur une seule question.",
  "Il ne connaît pas votre entreprise. Deux comptables au même score n'ont pas le même risque réel.",
  "Il vieillit. Un résultat vaut trois mois : les outils avancent plus vite que les fiches de poste.",
];

/*
  Les trois témoignages qui occupaient cette place étaient signés « — exemple ».
  C'était le pire des deux mondes : le lecteur qui remarquait la mention décotait
  toute la page, celui qui ne la remarquait pas était induit en erreur — et la
  place de la preuve était prise, donc la vraie n'avait nulle part où aller.
  Ce que nous pouvons démontrer aujourd'hui, ce sont nos limites. Elles ont pris
  leur place. Les témoignages reviendront quand ils seront réels.
*/

/** Progression des trois volets, et relance vers le volet manquant. */
function Stepper() {
  const { t1, t2, t3, goTo } = useApp();
  const done = [Boolean(t1), Boolean(t2), Boolean(t3)];
  const premierOuvert = done.indexOf(false);
  const n = done.filter(Boolean).length;

  const cle =
    n === 0
      ? "Aucun volet complété"
      : n === 3
        ? "Profil complet — 3 volets sur 3"
        : `${n} volet${n > 1 ? "s" : ""} sur 3`;

  let relance = "";
  if (n === 1 && !t2) {
    relance =
      "Il vous manque le chiffre : le volet 02 dit combien d'heures ces tâches vous prennent, et ce que ça vaut en francs.";
  } else if (n === 1 && t2) {
    relance = "Complétez le volet 01 pour savoir lesquelles de vos tâches sont concernées.";
  } else if (n === 2) {
    relance = "Un volet restant. Le profil complet débloque l'attestation vérifiable.";
  }

  const cartes: ReadonlyArray<{
    id: "s-t1" | "s-t2" | "s-t3";
    n: string;
    titre: string;
    texte: string;
    etat: string;
  }> = [
    {
      id: "s-t1",
      n: "01",
      titre: "Votre exposition",
      texte:
        "Vos tâches une par une : celles qu'une IA fait déjà, celles qu'elle fait en partie, celles qui restent à vous.",
      etat: t1 ? `Complété — ${t1.exposure.score}/100` : "Moins de deux minutes",
    },
    {
      id: "s-t2",
      n: "02",
      titre: "Le coût de votre temps",
      texte: "Les heures que ces tâches vous prennent chaque mois, et ce qu'elles valent en francs.",
      etat: t2 ? `Complété — ${fmt(t2.hours)} h/mois` : "1 minute",
    },
    {
      id: "s-t3",
      n: "03",
      titre: "Votre niveau de maîtrise",
      texte:
        /*
          Le nombre est compté, plus écrit — 26/08/2026.

          L'accueil promettait « huit mises en situation ». Le volet en pose
          seize depuis qu'il a doublé : deux questions par axe. La correction
          avait été faite **dans** le volet, où un contrôle la garde ; la
          promesse, elle, était restée à l'ancien chiffre. La personne
          s'engageait sur huit et en trouvait le double, à l'endroit précis où
          on lui demande de répondre sincèrement — un faux signal au sens de la
          règle n° 6.

          Il vient donc de la même source que les questions, et ne peut plus
          diverger d'elles.
        */
        `${MASTERY_AXES.length * 2} mises en situation, et une attestation vérifiable — à joindre à une candidature, à une proposition commerciale, ou à garder pour vous.`,
      etat: t3 ? `Complété — ${t3.level.libelle.split(" — ")[0]}` : "2 minutes",
    },
  ];

  return (
    <>
      <div className="stepper">
        {[0, 1, 2].map((i) => (
          <i key={i} className={done[i] ? "done" : i === premierOuvert ? "now" : ""} />
        ))}
      </div>
      <div className="stepper-key">{cle}</div>
      <div className="grid g3" style={{ marginTop: "28px" }}>
        {cartes.map((carte, i) => (
          <button
            key={carte.id}
            type="button"
            className={`tcard reveal${done[i] ? " done" : ""}`}
            onClick={() => goTo(carte.id)}
          >
            <div className="n">{carte.n}</div>
            <h3>{carte.titre}</h3>
            <p>{carte.texte}</p>
            <span className="state">{carte.etat}</span>
          </button>
        ))}
      </div>
      {relance !== "" && <div className="nudge">{relance}</div>}
      <div style={{ marginTop: "20px" }}>{n >= 2 && <SummaryCard />}</div>
    </>
  );
}

/** Récapitulatif des volets déjà complétés. */
export function SummaryCard() {
  const { t1, t2, t3 } = useApp();
  const lignes: ReadonlyArray<readonly [string, string]> = [
    ...(t1 ? ([["Indice d'exposition", `${t1.exposure.score}/100 · ${t1.exposure.gradient.libelle}`]] as const) : []),
    ...(t2 ? ([["Heures récupérables", `${fmt(t2.hours)} h/mois`]] as const) : []),
    ...(t2 ? ([["Valeur annuelle", `${fmt(t2.money)} F`]] as const) : []),
    ...(t3 ? ([["Niveau de maîtrise", t3.level.libelle]] as const) : []),
  ];

  return (
    <div className="summary">
      {lignes.map(([cle, valeur]) => (
        <div className="line" key={cle}>
          <span>{cle}</span>
          <b>{valeur}</b>
        </div>
      ))}
    </div>
  );
}

/*
  L'exemple de l'accueil est calculé, pas écrit — 28/09/2026.

  La vignette affichait « Assistant de direction, 78/100 », un chiffre posé à
  la main. Le même jour, une assistante de direction réelle obtenait 69 en
  production : l'exemple promettait un autre résultat que celui du produit.
  Et la page montrait un deuxième métier plus bas, le comptable de l'action
  offerte. Un seul fil désormais : le comptable de l'action et du spécimen,
  et son indice sort du barème publié. Si le barème change, l'exemple suit.
*/
const EXEMPLE_TACHES: ReadonlyArray<{ texte: string; niveau: TaskLevel; heures: number }> = [
  { texte: "Rapprochement bancaire mensuel", niveau: "high", heures: 8 },
  { texte: "Relance des clients en retard", niveau: "high", heures: 4 },
  { texte: "Préparation de la liasse fiscale", niveau: "mid", heures: 6 },
  { texte: "Relation avec la direction", niveau: "low", heures: 3 },
];
const EXEMPLE = computeExposure({ groupe: "COMPTA", taches: [...EXEMPLE_TACHES] });
const PASTILLE: Record<TaskLevel, { classe: string; libelle: string }> = {
  high: { classe: "c-high", libelle: "automatisable" },
  mid: { classe: "c-mid", libelle: "partiel" },
  low: { classe: "c-low", libelle: "résiste" },
};

export function Home({ onStartMetier }: { onStartMetier: (metier: string) => void }) {
  const { goTo, modeCv, aSonCompte, declarerStatut, prixEntreprise, parrainage, dirigeantActif } =
    useApp();
  const [heroJob, setHeroJob] = useState("");
  /*
    Le résultat précédent, rendu dès l'accueil — 01/10/2026.

    Le 31/08, l'écran de saisie du volet 1 a appris à dire, avant la saisie,
    qu'un second diagnostic était fermé. Mais le champ de l'accueil lance le
    diagnostic **sans passer par cet écran** : une session revenue avant terme
    y tapait son métier, attendait l'analyse, et recevait seulement alors le
    rappel de son résultat. C'était la porte la plus empruntée, et la seule qui
    ne savait rien. Le même texte, servi au même endroit que le champ.

    Le bouton reste actif : le refus du serveur fait foi, et un état lu au
    chargement peut avoir changé depuis — deux filleuls qui achèvent leur
    diagnostic pendant la visite rouvrent la porte. Ne pas savoir ne ferme rien.
  */
  const messageSecond = parrainage?.second ? messageSecondDiagnostic(parrainage.second) : null;
  const [heroInvalide, setHeroInvalide] = useState(false);
  useReveal([]);

  const lancer = () => {
    const valeur = heroJob.trim();
    if (valeur.length < 3) {
      setHeroInvalide(true);
      document.getElementById("hero-job")?.focus();
      return;
    }
    setHeroInvalide(false);
    track("cta_click", { from: "hero-go" });
    onStartMetier(valeur);
  };

  return (
    <main id="home" tabIndex={-1}>
      {/*
        L'ancre du lien d'évitement a quitté cet écran le 02/09/2026 : elle vit
        désormais sur le conteneur de vue d'`App.tsx`, qui existe sur les neuf
        vues du parcours. Ici, elle ne servait que l'accueil — et le même lien,
        sur les huit autres, ne menait à rien.
      */}

      {/* Avant tout le reste : celui qui revient chercher son rapport ne doit
          pas avoir à refaire un diagnostic pour trouver la porte. */}
      <RappelAchat />

      <div className="dark">
        <div className="shell hero">
          <div className="hero-grid">
            <div>
              {/*
                Les trois profils sont nommés **avant** le titre — 25/08/2026.

                « Diagnostic professionnel » ne disait à personne que le produit
                était pour lui. Le sélecteur, lui, est en bas du bloc : il règle
                le parcours mais il arrive après la promesse, et le dirigeant ne
                le voit qu'après avoir lu une page écrite pour un individu.

                Le premier mot lu est donc la liste des trois. C'est le moyen le
                plus court de répondre à « est-ce que c'est pour moi ? » — la
                seule question que se pose quelqu'un qui arrive d'un lien
                WhatsApp sans savoir ce qu'il ouvre.
              */}
              <div className="kicker">
                {dirigeantActif ? "Salariés · Indépendants · Dirigeants" : "Salariés · Indépendants"} —
                Afrique francophone
              </div>
              {/*
                « Votre place », et non « votre poste » — arbitrage du 25/08/2026.

                Un poste est un mot de salarié. La ligne la plus lue du produit
                excluait donc deux audiences sur trois dès sa première phrase,
                au-dessus de tout ce qu'on pouvait corriger plus bas.

                « Place » couvre les trois sans rien perdre : la place du salarié
                dans son entreprise, celle de l'indépendant chez ses clients,
                celle du dirigeant à la tête de la sienne. Et elle garde
                l'opposition qui fait la force de la ligne — **le statut d'un
                côté, le travail de l'autre**. « Métier » l'aurait émoussée :
                métier et tâches appartiennent au même registre, et l'on ne peut
                pas opposer deux mots du même monde.

                Le sous-titre suit : « écrivez ce que vous faites » vaut pour un
                poste comme pour une activité.
              */}
              <h1>
                L'IA ne prendra pas votre place. Elle prendra vos tâches, <i>une par une.</i>
              </h1>
              <p className="lede">
                {aSonCompte
                  ? "Écrivez ce que vous faites. En moins de deux minutes, vous saurez ce que vos clients peuvent déjà obtenir sans vous — et ce que vous pouvez produire en une heure au lieu de quatre."
                  : "Écrivez votre métier. En moins de deux minutes, vous saurez quelles tâches une IA sait déjà faire à votre place, lesquelles restent à vous, et quoi faire maintenant."}
              </p>
              <div className="hero-form">
                {/*
                  Les trois portes, au-dessus du champ — 25/08/2026.

                  Elles remplacent une case à cocher posée **sous le bouton** et
                  un lien en fin de bloc. Quiconque écrivait son métier et
                  appuyait ne les voyait jamais : le contrôle qui change tout le
                  produit arrivait après l'action qui termine l'interaction.

                  Ce n'est pas un écran d'aiguillage, et la nuance est tout le
                  sujet. Un écran de tri impose un choix avant que le visiteur
                  ait rien reçu — c'est un péage. Ici « salarié » est déjà
                  sélectionné : le cas de loin le plus fréquent ne paie rien, et
                  les deux autres se reconnaissent avant qu'on leur vende quoi
                  que ce soit.

                  Mélange assumé : deux réglages et une navigation dans le même
                  contrôle. « Je dirige » mène à `/dirigeant`, qui est un autre
                  parcours. C'est le prix pour que le livrable à 15 000 F cesse
                  d'être la ligne la moins visible de l'accueil.
                */}
                {messageSecond !== null && (
                  <div className="notice" role="status">
                    {messageSecond}
                  </div>
                )}
                <span className="lab-portes" id="hero-portes">
                  Vous êtes
                </span>
                <div className="portes" role="group" aria-labelledby="hero-portes" aria-describedby="hero-portes-aide">
                  <button
                    type="button"
                    aria-pressed={!aSonCompte}
                    onClick={() => declarerStatut(false)}
                  >
                    Salarié
                  </button>
                  <button
                    type="button"
                    aria-pressed={aSonCompte}
                    onClick={() => {
                      declarerStatut(true);
                      track("statut_independant", {});
                    }}
                  >
                    À mon compte
                  </button>
                  {dirigeantActif && (
                    <a href="/dirigeant" onClick={() => track("cta_click", { from: "porte-dirigeant" })}>
                      Je dirige <small>une entreprise</small>
                    </a>
                  )}
                </div>
                {/*
                  Le libellé d'abord, la règle ensuite — 28/09/2026. La phrase
                  se lisait à l'envers (« …chaque mois : Salarié »), au moment
                  précis où il faut choisir.
                */}
                <p className="fine" id="hero-portes-aide">
                  <b>Salarié</b> : un employeur vous paie. <b>À mon compte</b> : vos clients vous
                  paient, même si vous reversez une part à un propriétaire ou à un patron de
                  véhicule.
                </p>
                <label htmlFor="hero-job">
                  {aSonCompte ? "Votre activité — n'importe laquelle" : "Votre métier — n'importe lequel"}
                </label>
                <div className="fr">
                  <input
                    type="text"
                    id="hero-job"
                    placeholder={
                      aSonCompte ? "ex. graphiste, consultant…" : "ex. comptable, sage-femme…"
                    }
                    value={heroJob}
                    onChange={(e) => setHeroJob(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") lancer();
                    }}
                    style={heroInvalide ? { borderColor: "var(--brass)" } : undefined}
                    aria-invalid={heroInvalide}
                    aria-describedby={heroInvalide ? "hero-erreur hero-reassure" : "hero-reassure"}
                  />
                  {heroInvalide && (
                    <p id="hero-erreur" className="fine" role="alert" style={{ margin: "6px 0 0" }}>
                      Indiquez votre métier — trois lettres au moins.
                    </p>
                  )}
                  <button type="button" className="go" onClick={lancer}>
                    Voir mon indice
                  </button>
                </div>
                {/*
                  Les exemples viennent enfin de la liste écrite pour eux.

                  `SUGGESTS.slice(0, 5)` prenait les cinq premiers de la liste
                  brute — c'est-à-dire son premier bloc, « Bureau et gestion ».
                  Cinq métiers de bureau salariés : un chauffeur, une couturière,
                  une infirmière, un commerçant ne voyaient rien qui leur
                  ressemble, alors que tous quatre figurent dans la liste.

                  `SUGGESTS_VISIBLES` existait depuis le début, documentée pour
                  couvrir les sept familles, et n'était branchée nulle part. Les
                  exemples sont le signal le plus fort de « pour qui c'est » —
                  plus fort que n'importe quel titre.
                */}
                <div className="suggests">
                  {(aSonCompte ? SUGGESTS_INDEPENDANTS : SUGGESTS_VISIBLES).slice(0, 5).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => {
                        setHeroJob(s);
                        document.getElementById("hero-job")?.focus();
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                {/* Sous les garanties, pas au-dessus : le chiffre appuie la
                    promesse, il ne la remplace pas. */}
                <Usage />
                {/* La case « à mon compte » et le lien dirigeant vivaient ici, sous
                    le bouton. Ils sont remontés dans le sélecteur au-dessus du
                    champ : posés après l'action qui termine l'interaction, ils
                    n'étaient vus que par ceux qui ne s'en servaient pas. */}
                <div className="reassure" id="hero-reassure">
                  <span>Gratuit</span>
                  <span>Anonyme</span>
                  <span>Moins de deux minutes</span>
                  {/*
                  « Rien n'est conservé » n'était pas vrai — 26/08/2026.

                  La politique de confidentialité, deux écrans plus bas, dit
                  l'inverse en toutes lettres : le résultat agrégé et anonyme
                  **est** conservé pour le calcul des comparaisons, et le
                  navigateur retient un nombre durable qui fait fonctionner le
                  parrainage. Ce qui n'est pas conservé, c'est le texte du CV,
                  et ce qui n'est jamais demandé, c'est ce qui vous désigne.

                  Un badge qui contredit la politique de confidentialité du même
                  site est un faux signal au sens de la règle n° 6 — et il porte
                  sur la promesse même qui décide quelqu'un à essayer.
                */}
                  <span>Rien qui vous désigne</span>
                </div>
              </div>
              {/*
                Ce qui a été retiré ici, et pourquoi — 24/08/2026.

                Deux blocs de texte suivaient les puces de réassurance. Le
                premier — « votre employeur n'est jamais contacté, votre nom
                n'est pas demandé » — répétait mot pour mot les puces qui le
                précédaient de vingt pixels, et une troisième fois la question
                du même nom dans la foire aux questions. Le second récitait la
                dérivation de l'OIT, que la section « méthode » développe plus
                bas et que le pied de page cite en entier.

                Sur un téléphone, ces deux blocs repoussaient hors de l'écran le
                champ de saisie et les métiers suggérés. **Le premier geste
                qu'on demande passait sous le bavardage censé le justifier.**

                Ce qui reste tient en une ligne et pointe vers la preuve au lieu
                de la résumer — « dérivé de » se vérifie, et le lien permet de
                le faire. « Adossé à » serait faux et offrirait la réfutation à
                qui vérifie.
              */}
              <p className="trust">
                Calcul dérivé de l'indice de l'Organisation internationale du Travail (OIT).{" "}
                <a href="/methode">La méthode est publiée</a> : vous pouvez refaire le calcul.
              </p>
            </div>
            {/* Panneau d'illustration : une maquette, pas un résultat. Il est dit.
                La vignette nommait une ville — 25/08/2026, elle nomme le métier.
                Le produit s'adresse à toute l'Afrique francophone : afficher
                « Abidjan » sur l'exemple d'accueil disait à un lecteur de Dakar
                ou de Lomé que l'exemple n'était pas le sien. Le métier, lui,
                dit quelque chose du diagnostic. */}
            <div className="instrument" aria-hidden="true">
              <div className="head">
                <span>Extrait de diagnostic — exemple</span>
                <b>Comptable</b>
              </div>
              <div className="body">
                <div className="big">
                  {EXEMPLE.score}
                  <small>/100</small>
                </div>
                <div className="g">{EXEMPLE.gradient.libelle}</div>
                {EXEMPLE_TACHES.map((t) => (
                  <div className="pline" key={t.texte}>
                    <span>{t.texte}</span>
                    <i className={`chip ${PASTILLE[t.niveau].classe}`}>{PASTILLE[t.niveau].libelle}</i>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <Ticker />
      </div>


      <section className="band" id="diagnostic">
        <div className="shell">
          <div className="kicker reveal">Le diagnostic · trois étapes</div>
          <h2 className="reveal">Savoir, chiffrer, se situer.</h2>
          <p className="lede reveal">
            Chaque étape est gratuite et se suffit à elle-même. Le rapport reprend les trois.
          </p>
          <Stepper />
        </div>
      </section>


      {/*
        L'ordre de la page, et pourquoi il a changé.

        La démonstration — une action entière, telle qu'elle serait livrée —
        commençait à l'écran 6, et les tarifs à l'écran 12. Sur une page
        d'accueil, la moitié des visiteurs s'arrêtent vers l'écran 2 et moins
        d'un sur dix atteint l'écran 8 : le meilleur argument de vente et le
        prix étaient donc tous deux hors de portée. Quelqu'un convaincu à
        l'écran 3 n'avait nulle part où acheter avant neuf écrans de
        défilement.

        L'ordre est désormais celui d'une conversation : on montre ce qu'on
        livre, on donne le prix, puis on argumente pour ceux qui hésitent
        encore. Rien n'a été supprimé — ce qui suit reste lisible pour qui
        descend.
      */}
      {/*
        L'aperçu de l'action a sa propre section, et il passe avant « Le rapport ».

        Il vivait à la fin d'une section de 3 902 px — quatre écrans et demi de
        téléphone — qui portait déjà deux autres sujets sous l'étiquette « Le
        rapport ». Il était en outre le seul bloc du site sans surtitre : donc
        invisible à qui balaie la page, alors que c'est la seule démonstration
        gratuite du produit. Et il arrivait après « nos limites », c'est-à-dire
        qu'on disait ce que le produit ne fait pas avant d'avoir montré ce
        qu'il fait.
      */}
      <section className="band" id="apercu">
        <div className="shell">
          <div className="kicker reveal">Un échantillon, pas une promesse</div>
          <h2 className="reveal">Voici une action entière, telle qu'elle vous serait livrée</h2>
          <ApercuAction />
        </div>
      </section>

      {/* La preuve avant le prix — et rien tant qu'aucun témoignage réel et
          accordé n'existe (voir `content/temoignages.ts`). */}
      <Temoignages />

      <section className="band" id="tarifs">
        <div className="shell">
          <div className="kicker reveal">Tarifs</div>
          <h2 className="reveal">
            Le diagnostic est gratuit. Vous ne payez qu'après avoir vu votre indice.
          </h2>
          <p className="lede reveal">
            Un seul rapport, quel que soit le volet par lequel vous êtes entré. Aucun abonnement,
            aucun prélèvement automatique.
          </p>
          <Offers />
          {/*
            Le second prix manquait à la section qui s'appelle « Tarifs ».

            Un dirigeant qui descendait jusqu'ici lisait 3 000 F et en concluait
            que c'était le prix. Il découvrait les 15 000 F au bout du
            questionnaire — exactement le défaut que la page dirigeant vient de
            réparer chez elle, et qui se rejouait ici.

            Deux prix sur un même site se tiennent parce que les deux livrables
            diffèrent : la ligne dit donc ce qu'on obtient en plus, pas
            seulement combien on paie.
          */}
          {dirigeantActif && (
            <>
              <p className="fine reveal" style={{ marginTop: "18px" }}>
                <b>Vous dirigez une entreprise ?</b> Le rapport de votre entreprise — dix actions à
                confier à vos équipes et le plan des trente premiers jours — est à{" "}
                {fmt(prixEntreprise ?? PLANS.entreprise)} F.
              </p>
              {/* Sur sa propre ligne : un lien pris dans un paragraphe fait 33 px
                  de haut là où la cible en exige 44, et se rate au pouce. Même
                  classe que sur la page dirigeant. */}
              <a className="lien-cible reveal" href="/dirigeant">
                Voir le diagnostic dirigeant
              </a>
            </>
          )}
          <p className="fine reveal" style={{ marginTop: "20px" }}>
            <b>Remboursé sans discussion</b>, sur simple demande, dans les quatorze jours et sans
            justification à fournir.
          </p>
          {/*
            Replié depuis le 02/10/2026 : quatre étapes de texte entre le prix et
            la suite de la page. Le délai reste dit sur la carte de prix ; le
            détail s'ouvre pour qui hésite.
          */}
          <details className="deplie">
            <summary>Ce qui se passe après le paiement</summary>
            <ApresPaiement />
          </details>
          {/*
            La porte de récupération, au pied de la section des tarifs.

            Placée là parce que c'est là que descend quelqu'un qui cherche à
            racheter ce qu'il a déjà payé — le réflexe de celui qui a perdu son
            lien est de revenir sur la page du prix. Repliée par défaut : elle
            ne concerne qu'une personne sur mille, et une entrée de mot de passe
            visible sur un site sans compte inquiète plus qu'elle ne rassure.
          */}
          <RecupererRapport />
        </div>
      </section>

      {/*
        Les deux blocs d'argumentation passent sous un dépliant.

        Ils vivent aux écrans 9 à 13, où presque personne n'arrive : les replier
        ne coûte donc rien à ceux qui ne les lisaient pas, et rend la page
        parcourable pour ceux qui descendent. Le **titre reste visible** — c'est
        lui qui porte l'argument ; ce qui se replie est la démonstration
        détaillée, que l'on ouvre si l'on hésite encore.

        Ce n'est pas une suppression : le contenu est dans la page, indexé,
        atteignable en un geste.
      */}
      <section className="band dark" id="comparatif">
        <div className="shell">
          <div className="kicker">Pourquoi nous, et pas autre chose</div>
          <h2>
            Vous n'avez pas besoin d'une formation. Vous avez besoin de savoir où vous en êtes.
          </h2>
          <details className="deplie">
            <summary>Voir la comparaison</summary>
          <p className="lede">
            Le marché vous propose des vidéos gratuites qui expliquent des outils, ou des formations
            à 150 000 F qui vous apprennent à écrire un prompt. Aucun des deux ne vous dit ce que{" "}
            <i>votre</i> travail devient.
          </p>
          {/*
            Le tableau est piloté par les données pour que chaque cellule porte
            le nom de sa colonne : sous 640 px il se replie en cartes, une par
            critère, et cet attribut devient l'étiquette de la valeur. Quatre
            colonnes serrées à 13 px laissaient environ soixante-quinze pixels
            pour « 80 000 à 250 000 F » — illisible sur l'argument le plus fort
            de la page, dans un produit conçu pour le mobile d'abord.
          */}
          <table className="compare">
            <thead>
              <tr>
                <th />
                {COLONNES.map((colonne, i) => (
                  <th key={colonne} className={i === COLONNES.length - 1 ? "us" : undefined}>
                    {colonne}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARATIF.map((ligne) => (
                <tr key={ligne.critere}>
                  <th>{ligne.critere}</th>
                  {ligne.cellules.map(([texte, ton], i) => (
                    <td
                      key={COLONNES[i]}
                      data-col={COLONNES[i]}
                      className={[i === COLONNES.length - 1 ? "us" : "", ton]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {texte}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          </details>
        </div>
      </section>

      {/*
        « Le rapport », « Nos limites » et « La méthode » réunis — 02/10/2026.

        Trois sections, trois écrans et demi de téléphone, lus par presque
        personne : « Le rapport » reprenait en prose la carte de prix juste
        au-dessus, « Nos limites » et « La méthode » sont justes mais leur
        accumulation faisait douter plutôt que rassurer. Les titres restent
        visibles, et ce sont eux qui portent l'argument ; le détail se déplie.
        Rien n'est retiré : tout est dans la page, et la méthode entière vit
        sur `/methode`.

        Les deux chiffres de la méthode — les professions notées par l'OIT et
        l'écart entre nos groupes — viennent ici de l'ancienne rangée de tuiles,
        toujours lus dans le barème publié, jamais écrits à la main.
      */}
      <section className="band dark" id="methode">
        <div className="shell narrow">
          <div className="kicker">La méthode</div>
          <h2>Un calcul public, que vous pouvez refaire</h2>
          <p className="lede">
            Nos onze groupes de métiers sont notés à partir des {PROFESSIONS_NOTEES} professions
            notées une par une par l'Organisation internationale du Travail. Selon le métier, la
            base va de {BASE_MIN} à {BASE_MAX} sur cent : ce qui décide, c'est le métier, pas la
            personne. Et aucune donnée qui vous désigne n'est nécessaire.
          </p>
          <details className="deplie">
            <summary>Le détail de la méthode</summary>
            <p className="fine">
              Nous publions le tableau qui relie chaque profession à l'un de nos onze groupes, la
              transformation appliquée aux scores, la formule, les poids et le barème complet : vous
              pouvez refaire le calcul, y compris pour le contester. Notre travail est une adaptation
              de celui de l'OIT, il n'est ni validé ni approuvé par elle. Ce que le score mesure, ce
              sont les tâches que vous décrivez et le temps que vous déclarez y passer — il vous est
              utile, il ne constitue pas une preuve opposable à un tiers.
            </p>
            {/* Sur sa propre ligne : `.lien-cible` porte les quarante-quatre pixels
                exigés au pouce, ce qu'un lien pris dans une phrase ne peut pas. */}
            <p style={{ margin: "4px 0 0" }}>
              <a className="lien-cible" href="/donnees/citp08-vers-groupes.csv" download>
                Télécharger le tableau de passage (CSV)
              </a>
            </p>
          </details>
          <details className="deplie">
            <summary>Ce que contient le rapport</summary>
            <p>
              Une vingtaine de pages, établies sur les tâches que vous avez décrites et les heures
              que vous avez déclarées. Les dix actions forment un ordre d'exécution, pas une liste de
              conseils : chacune dit pourquoi elle passe avant les autres, ses étapes, le texte à
              copier dans l'assistant, le piège à éviter et la façon de vérifier que c'est réussi.
              Les cinq automatisations tournent sur une connexion ordinaire et sans budget logiciel.{" "}
              {aSonCompte
                ? "Le repositionnement porte sur votre offre — ce que vous vendez, à qui, à quel prix — à partir de vos clients réels, pas d'un modèle d'activité."
                : "La trajectoire de repositionnement part de votre expérience, pas d'un métier idéal."}{" "}
              Et une date, dans quatre-vingt-dix jours, avec ce qu'il faudra comparer ce jour-là.
            </p>
          </details>
          <details className="deplie">
            <summary>Ce que ce diagnostic ne fait pas</summary>
            <ul className="listy">
              {LIMITES.map((limite) => (
                <li key={limite}>
                  <span>{limite}</span>
                </li>
              ))}
            </ul>
          </details>
        </div>
      </section>


      <section className="band" style={{ background: "var(--bone-2)" }} id="faq">
        <div className="shell narrow">
          <div className="kicker reveal">Questions fréquentes</div>
          <h2 className="reveal">Ce qu'on nous demande le plus</h2>
          <div style={{ marginTop: "26px" }}>
            {FAQ.filter(
              ({ question }) =>
                (modeCv === true || question !== FAQ_CV) &&
                (dirigeantActif || question !== FAQ_DIRIGEANT),
            ).map(
              ({ question, reponse, lien }) => (
                <details
                  className="faq"
                  key={question}
                  onToggle={(e) => {
                    if ((e.currentTarget as HTMLDetailsElement).open) {
                      track("faq_open", { q: question });
                    }
                  }}
                >
                  <summary>{question}</summary>
                  <p>{avecPrixEntreprise(reponse, prixEntreprise ?? PLANS.entreprise)}</p>
                  {lien !== undefined && (
                    <p style={{ margin: "10px 0 0" }}>
                      <a
                        className="lien-cible"
                        href={lien.href}
                        onClick={() => track("cta_click", { from: `faq:${lien.href}` })}
                      >
                        {lien.texte}
                      </a>
                    </p>
                  )}
                </details>
              ),
            )}
          </div>
        </div>
      </section>

      <section className="band dark">
        <div className="shell narrow" style={{ textAlign: "center" }}>
          <h2>Vos tâches sont déjà en train de changer de main.</h2>
          <p className="lede" style={{ margin: "0 auto 30px" }}>
            Deux minutes pour savoir lesquelles. Moins de temps qu'il ne vous en faudra pour
            refaire, cette semaine, ce que vous refaites déjà chaque semaine.
          </p>
          <button
            type="button"
            className="btn btn-brass"
            style={{ maxWidth: "340px", margin: "0 auto" }}
            onClick={() => {
              track("cta_click", { from: "faq-cta" });
              goTo("s-t1");
            }}
          >
            Lancer mon diagnostic
          </button>
        </div>
      </section>
    </main>
  );
}
